import { Component, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CtWebapiGenericResponse, CtWebapiService, DataRequest } from '@ctsolution/ct-webapi';
import {
  CtBinaryOperator,
  CTDynamicFormGeneratorComponent,
  CtDynamicFormGeneratorConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelService
} from '@ctsolution/ct-framework';
import { CTMGeneralService } from '../../../_core/lib/general.service';
import { FileGet } from '../../../_core/classes/file-get';
import { FormModuleData } from '../../../_core/classes/form-module-data';
import { FormModuleDynamicParameter } from '../../../_core/classes/form-module-dynamic-parameter';
import { StatusFormModuleData } from '../../../_core/enum/status-form-module-data';
import { FormModuleDataRouteData } from '../../../_core/route-data/form-module-data.route-data';
import { UserInfoFormTemplateRouteData } from '../../../_core/route-data/user-info-form-template.route-data';

/**
 * Dettaglio di un CTFormTemplate associato all'utente.
 *
 * - Carica il template via "FormTemplate/Detail/{oid}" (CtModelService.getInfos).
 * - Nella risposta la proprietà `Files` è un array di FileGetDTO: ogni DTO ha
 *   `Endpoint` (url del file). Il PDF viene recuperato come blob autenticato e
 *   mostrato in un iframe (sola visualizzazione, nessun download).
 */
@Component({
  selector: 'app-profile-form-template-detail',
  templateUrl: './profile-form-template-detail.component.html',
  styleUrls: ['./profile-form-template-detail.component.scss']
})
export class ProfileFormTemplateDetailComponent implements OnInit {

  isLoading = true;
  pdfLoading = false;
  error: string | null = null;

  templateName: string | null = null;
  files: FileGet[] = [];
  selectedFile: FileGet | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  /** Form dinamico generato dal template (come form-module-data/edit). */
  formConfiguration: CtDynamicFormGeneratorConfiguration | null = null;

  /** true se il template non ha una definizione form (JSON vuoto) → sola "presa visione". */
  acknowledgeOnly = false;

  /** true se per questo modulo esiste già un record (form compilato o presa visione registrata). */
  acknowledged = false;

  @ViewChildren('formGenerator') formGeneratorComponents: QueryList<CTDynamicFormGeneratorComponent> | null = null;

  @ViewChild('confirmPresaVisioneDialog') confirmPresaVisioneDialogTpl: TemplateRef<any> | null = null;

  private currentObjectUrl: string | null = null;
  private templateOid: number | null = null;
  private moduleDataSource: FormModuleData | null = null;
  /** Oid del record UserInfo_FormTemplate (associazione utente↔modulo) su cui scrivere ReadCompletedDate. */
  private userInfoFormTemplateOid: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private generalService: CTMGeneralService,
    private webapi: CtWebapiService,
    private modelService: CtModelService<any>,
    private dialog: MatLegacyDialog,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {

    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.back();
      return;
    }

    const uft = this.route.snapshot.queryParamMap.get('uftOid');
    this.userInfoFormTemplateOid = uft ? +uft : null;

    this.loadDetail(+id);

  }

  /**
   * Recupera il dettaglio del template (FormTemplate/Detail/{oid}) e la lista dei file.
   */
  private loadDetail(oid: number): void {

    this.isLoading = true;
    this.error = null;

    this.generalService
      .getInfos({ controller: 'FormTemplate', oid })
      .subscribe({
        next: (response: any) => {

          const data = response?.Result?.DataSource;

          this.templateOid = oid;
          this.templateName = data?.Name ?? data?.Title ?? data?.Description ?? data?.Code ?? `Modulo #${oid}`;
          this.files = (data?.Files ?? []) as FileGet[];

          // Titolo di pagina (breadcrumb) = nome del template
          this.route.data.subscribe(routeData => {
            routeData['title'] = this.templateName;
            routeData['urls'] = [{ title: this.templateName }];
          });

          // Seleziona di default il primo PDF (o il primo file disponibile).
          const firstPdf = this.files.find(f => (f.FileName ?? '').toLowerCase().endsWith('.pdf'));
          const first = firstPdf ?? this.files[0] ?? null;

          if (first) {
            this.selectFile(first);
          }

          // Il dettaglio è pronto: mostralo comunque, anche se il form dinamico dovesse fallire.
          this.isLoading = false;

          // Form dinamico generato dal template + eventuale compilazione esistente.
          // Protetto: un template "difficile" non deve impedire l'apertura del modulo.
          try {
            if (data) {
              this.setupDynamicForm(data, oid);
            }
          } catch (e) {
            console.error('Impossibile costruire il form dinamico per questo modulo:', e);
          }

        },
        error: (err) => {
          console.error('Errore durante il caricamento del modulo:', err);
          this.error = 'Errore durante il caricamento del modulo.';
          this.isLoading = false;
        }
      });

  }

  /**
   * Scarica il file (blob autenticato) dall'Endpoint del FileGetDTO e lo mostra in iframe.
   */
  selectFile(file: FileGet): void {

    if (!file?.Endpoint) return;

    this.selectedFile = file;
    this.pdfLoading = true;
    this.error = null;
    this.revokeCurrentObjectUrl();
    this.pdfUrl = null;

    // Stesso pattern provato di downloadAttachmentByEndPoint: GET sull'Endpoint come blob.
    const request = DataRequest
      .create()
      .setUrl(file.Endpoint)
      .setResponseType('blob');

    this.webapi
      .get(request)
      .subscribe({
        next: (blob: Blob) => {

          // Forziamo il MIME application/pdf così l'iframe lo renderizza inline
          // (alcuni endpoint restituiscono octet-stream → altrimenti parte il download).
          const isPdf = (file.FileName ?? '').toLowerCase().endsWith('.pdf');
          const typedBlob = new Blob([blob], { type: isPdf ? 'application/pdf' : (blob.type || 'application/pdf') });

          this.currentObjectUrl = URL.createObjectURL(typedBlob);
          // #toolbar=0&navpanes=0: nasconde la toolbar del viewer PDF (download, stampa)
          // → documento in sola visualizzazione, non scaricabile dall'anteprima.
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.currentObjectUrl}#toolbar=0&navpanes=0`);
          this.pdfLoading = false;

        },
        error: (err: any) => {
          console.error('Impossibile caricare il documento:', err);
          this.error = 'Impossibile caricare il documento.';
          this.pdfLoading = false;
        }
      });

  }

  /**
   * Costruisce il form dinamico dal template e carica l'eventuale compilazione
   * esistente (formmoduledata) per questo template — stessa logica di form-module-data/edit.
   */
  private setupDynamicForm(templateModel: any, oid: number): void {

    // Se il template NON ha una definizione form (JSON vuoto) → niente form dinamico:
    // si mostra solo il PDF e il bottone "Presa visione".
    const hasFormDefinition = !!templateModel?.JSONContent;
    this.acknowledgeOnly = !hasFormDefinition;

    if (hasFormDefinition) {

      const configuration = CtDynamicFormGeneratorConfiguration
        .create()
        .setFormGeneratorModel(templateModel);

      configuration.setOnSubmit((filteredValue: any) => {

        const parameter = FormModuleDynamicParameter
          .create()
          .setJSONContent(JSON.stringify(filteredValue.value))
          .setFormGeneratorOid(oid);

        if (this.moduleDataSource?.Oid) {
          parameter.setOid(this.moduleDataSource.Oid);
        }

        if (this.moduleDataSource?.Status) {
          parameter.setStatus(this.moduleDataSource.Status);
        }

        this.saveModuleData(parameter);

      });

      this.formConfiguration = configuration;

    }

    // Carica l'eventuale record formmoduledata già compilato / preso visione per questo template.
    const operators = CtModelDatatableOperators
      .create()
      .setFilters([
        CTModelDatatableFilter
          .create()
          .setField('FormTemplate.Oid')
          .setValue(oid)
          .setOperatorType(CtBinaryOperator.Equal)
      ]);

    this.modelService
      .getList(this.formModuleDataConfiguration(), operators)
      .subscribe({
        next: (response: CtWebapiGenericResponse<any>) => {

          const list = (response?.Result?.DataSource ?? []) as any[];

          if (!list.length) return;

          this.moduleDataSource = FormModuleData.create(list[0]);
          this.acknowledged = true; // esiste già un record: form compilato o presa visione registrata

          if (this.formConfiguration && this.moduleDataSource.JSONContent) {
            try {
              const parsed = JSON.parse(this.moduleDataSource.JSONContent);
              setTimeout(() => this.setChildFormData(parsed));
            } catch (e) {
              console.error('JSONContent del modulo non valido:', e);
            }
          }

          if (this.moduleDataSource.Status === StatusFormModuleData.Validato) {
            this.formConfiguration?.setDisableAll(true);
          }

        },
        error: (err) => console.error('Errore durante il caricamento dei dati del modulo:', err)
      });

  }

  private formModuleDataConfiguration(): CtModelConfiguration<any> {
    return CtModelConfiguration.create().setRouteData(FormModuleDataRouteData());
  }

  private setChildFormData(value: any): void {
    const componentRef = this.formGeneratorComponents?.toArray()?.[0];
    if (componentRef) {
      componentRef.form.patchValue(value);
    }
  }

  private saveModuleData(parameter: FormModuleDynamicParameter): void {

    const configuration = this.formModuleDataConfiguration();

    if (parameter.Oid) {
      configuration.RouteData?.setId(parameter.Oid);
    }

    this.modelService
      .putInfos(configuration, parameter)
      ?.subscribe({
        next: (response: CtWebapiGenericResponse<number | null>) => {

          // Aggiorna l'Oid così i salvataggi successivi diventano update (no duplicati).
          if (response?.Result && !isNaN(response.Result)) {
            if (!this.moduleDataSource) this.moduleDataSource = FormModuleData.create();
            this.moduleDataSource.Oid = response.Result;
          }

          // Dopo salva/presa visione: registra la data di lettura completata.
          this.markReadCompleted();

        },
        error: (err) => console.error('Errore durante il salvataggio del modulo:', err)
      });

  }

  /**
   * Valorizza il campo ReadCompletedDate del record UserInfo_FormTemplate (associazione
   * utente↔modulo) con la data del salvataggio, poi lo aggiorna sul relativo controller.
   */
  private markReadCompleted(): void {

    const oid = this.userInfoFormTemplateOid;
    if (!oid) return;

    // Read-modify-write: recupero il DTO, imposto la data, salvo (Update).
    this.generalService
      .getInfos({ controller: UserInfoFormTemplateRouteData().controller!, oid })
      .subscribe({
        next: (response: any) => {

          const dto = response?.Result?.DataSource;
          if (!dto) return;

          dto.ReadCompletedDate = new Date().toISOString();

          const configuration = CtModelConfiguration
            .create()
            .setRouteData(UserInfoFormTemplateRouteData().setId(oid)); // id ⇒ Update

          this.modelService
            .putInfos(configuration, dto)
            ?.subscribe({
              next: () => { },
              error: (err: any) => console.error('Errore durante l\'aggiornamento di ReadCompletedDate:', err)
            });

        },
        error: (err: any) => console.error('Errore durante il recupero di UserInfo_FormTemplate:', err)
      });

  }

  /**
   * Modulo senza form (JSON vuoto): apre una modale di conferma; la presa visione
   * viene registrata SOLO alla conferma.
   */
  presaVisione(): void {

    if (!this.templateOid || !this.confirmPresaVisioneDialogTpl) return;

    this.dialog
      .open(this.confirmPresaVisioneDialogTpl, { width: '420px', autoFocus: false })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.confirmPresaVisione();
        }
      });

  }

  /** Registra la presa visione (formmoduledata con contenuto vuoto) — eseguito alla conferma. */
  private confirmPresaVisione(): void {

    if (!this.templateOid) return;

    const parameter = FormModuleDynamicParameter
      .create()
      .setJSONContent('{}')
      .setFormGeneratorOid(this.templateOid);

    if (this.moduleDataSource?.Oid) {
      parameter.setOid(this.moduleDataSource.Oid);
    }

    this.saveModuleData(parameter);
    this.acknowledged = true;

  }

  back(): void {
    this.router.navigate(['/', 'profile']);
  }

  private revokeCurrentObjectUrl(): void {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }
  }

}
